/**
 * Auth Service
 *
 * Authentication API client - extends base API client with auth-specific methods.
 * Part of the Identity domain - handles authentication for the identity domain.
 *
 * Architecture:
 * - This is domain-specific business logic (API calls to /identity/auth/*)
 * - Belongs in services/domains/identity/ (not services/shared/infrastructure/)
 * - React components (AuthProvider) are in features/identity/providers/
 */

import { ApiClient } from "@/services/shared/infrastructure/api-client";
import { TokenStorage } from "@/services/shared/infrastructure/storage";
import type { ApiResponse } from "@/services/shared/infrastructure/types";
import { toAuthErrorMessage } from "./auth-errors";
import {
  clearRememberMePreference,
  defaultAccessTokenTtl,
  defaultRefreshTokenTtl,
  isRememberMeSession,
  setRememberMePreference,
  ttlFromJwt,
} from "./auth-session";

export class AuthApiClient extends ApiClient {
  /**
   * Login - removed orgId parameter (security: orgId must come from backend token)
   * Backend must return: { accessToken, refreshToken?, orgIds, userId, email, role }
   *
   * SECURITY: Backend should set refreshToken as httpOnly cookie
   * @param rememberMe - If true, tokens will have longer TTL (30 days for refresh, 24 hours for access)
   */
  async login(email: string, password: string, rememberMe: boolean = false) {
    try {
      setRememberMePreference(rememberMe);

      const response = await this.post<{
        accessToken: string;
        refreshToken?: string;
        orgIds: string[];
        userId: string;
        email: string;
        role: string;
      }>("/identity/auth/sessions", { email, password, rememberMe });

      if (response.data.accessToken) {
        const accessTokenTTL = ttlFromJwt(
          response.data.accessToken,
          defaultAccessTokenTtl(rememberMe),
        );
        const refreshTokenTTL = response.data.refreshToken
          ? ttlFromJwt(
              response.data.refreshToken,
              defaultRefreshTokenTtl(rememberMe),
            )
          : defaultRefreshTokenTtl(rememberMe);

        this.setToken(response.data.accessToken, accessTokenTTL);

        if (response.data.refreshToken) {
          this.setRefreshToken(response.data.refreshToken, refreshTokenTTL);
        }
      } else {
        throw new Error("No token received from server");
      }
      return response;
    } catch (error: unknown) {
      clearRememberMePreference();
      throw new Error(toAuthErrorMessage(error));
    }
  }

  /**
   * Get current user - backend must return sanitized user data
   * Expected: { orgId, userId, email, role }
   *
   * Note: This extracts orgId and userId from the JWT token to construct the user endpoint
   */
  async getMe() {
    const token = this.token;

    if (!token) {
      throw new Error("No authentication token available");
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1] || ""));
      const orgId = payload.orgId || (payload.orgIds && payload.orgIds[0]);
      const userId = payload.userId;

      if (!orgId || !userId) {
        throw new Error("Token missing orgId or userId");
      }

      const endpoint = `/orgs/${orgId}/identity/users/${userId}`;

      const response = await this.get<{
        orgIds: string[];
        userId: string;
        email: string;
        role: string;
      }>(endpoint);

      return response;
    } catch {
      throw new Error("Invalid token format");
    }
  }

  /**
   * Refresh token - backend rotates refresh token and returns accessToken.
   * SECURITY: Backend may read refreshToken from httpOnly cookie when credentials are included.
   */
  async refreshToken(): Promise<void> {
    try {
      const rememberMe = isRememberMeSession();
      const refreshTokenPayload = this.refreshTokenValue
        ? { refreshToken: this.refreshTokenValue }
        : {};

      let orgIdFromToken: string | null = null;
      if (this.refreshTokenValue) {
        try {
          const payload = JSON.parse(
            atob(this.refreshTokenValue.split(".")[1] || ""),
          );
          if (
            payload.orgIds &&
            Array.isArray(payload.orgIds) &&
            payload.orgIds.length > 0
          ) {
            orgIdFromToken = payload.orgIds[0];
          } else if (payload.orgId) {
            orgIdFromToken = payload.orgId;
          }
        } catch {
        }
      }

      const response = await this.post<{
        accessToken: string;
        refreshToken?: string;
        orgIds: string[];
      }>("/identity/auth/sessions/refresh", refreshTokenPayload, {
        headers: orgIdFromToken ? { "X-Org-Id": orgIdFromToken } : undefined,
      });

      const accessToken = response.data?.accessToken;
      if (accessToken) {
        const accessTokenTTL = ttlFromJwt(
          accessToken,
          defaultAccessTokenTtl(rememberMe),
        );
        const refreshTokenTTL = response.data.refreshToken
          ? ttlFromJwt(
              response.data.refreshToken,
              defaultRefreshTokenTtl(rememberMe),
            )
          : defaultRefreshTokenTtl(rememberMe);

        this.setToken(accessToken, accessTokenTTL);

        if (response.data.refreshToken) {
          this.setRefreshToken(response.data.refreshToken, refreshTokenTTL);
        }
      } else {
        throw new Error("No token received from refresh endpoint");
      }
    } catch (error: unknown) {
      this.setToken(null);
      this.setRefreshToken(null);
      clearRememberMePreference();

      throw new Error(toAuthErrorMessage(error));
    }
  }

  logout(): void {
    clearRememberMePreference();
    super.logout();
  }
}

// Export singleton instance
export const authApiClient = new AuthApiClient();
